import React, { useEffect, useMemo, useState } from "react";
import ContentBlock from "../Layout/ContentBlock";
import TableFilterBox from "../common/TableFilterBox";
import ContentValidation from "../common/ContentValidation";
import { useDispatch, useSelector } from "react-redux";
import { Label, SelectCountry, SelectInput } from "../globals";
import TableUi from "../Layout/TableUI";
import { CheckBoxInput } from "../common/Buttons";
import {
  IconEE,
  IconFI,
  IconLT,
  IconLV,
  IconPackageBox,
  IconTag,
} from "../Layout/Icons";
import { onChangeUseState } from "../../helpers/FunctionsGlobal";
import { Tooltip } from "antd";
import Spinner from "../common/Spinner";
import { getImumMonitorings } from "../../redux/Actions/imum_monitorings_action";
import { AiOutlineExclamation } from "react-icons/ai";
import { removeDuplicates } from "../../helpers/array";
import { formatDateTime } from "../../utils/date";

const defaultLangs = ["LT", "LV", "EE", "FI"];

const filterInitial = {
  countries_in: defaultLangs,
  categories_in: undefined,
  items_products: true,
  items_prices_changes: true,
  only_failure: false,
};

const ImumTrackingCategories = () => {
  const dispatch = useDispatch();
  const { loading, data, current, show_data } = useSelector(
    (state) => state.custom
  );
  const [filters, setFilters] = useState({
    ...filterInitial,
    countries_in: window.identifier == "technorama" ? ["LT"] : defaultLangs,
  });

  useEffect(() => {
    dispatch(getImumMonitorings());
  }, []);

  const initialColumns = useMemo(() => {
    if (current !== "imum_monitorings") return [];
    if (!data) return [];
    if (!Array.isArray(data)) return [];
    if (!data.length) return [];
    return removeDuplicates(data.map(({ stat_date }) => stat_date));
  }, [data]);

  const monitorings = useMemo(() => {
    if (!data) return [];
    if (!Array.isArray(data)) return [];
    if (!data.length) return [];

    let categories = show_data?.categories ? [...show_data?.categories] : [];

    if (filters.categories_in && filters.categories_in.length > 0) {
      categories = categories.filter(({ value }) =>
        filters.categories_in.includes(value)
      );
    }

    const value = categories.map((category) => {
      const filtered = data.filter(
        (value) => value.category === category.value
      );
      return {
        category: category.value,
        dates: initialColumns
          .map((date) => ({
            date,
            items: filtered
              .filter((value) => date == value.stat_date)
              .filter((value) =>
                filters.countries_in.includes(value.country.toUpperCase())
              )
              .map((values) => {
                const dayPast = new Date(values.stat_date);
                dayPast.setDate(dayPast.getDate() - 1);
                return {
                  ...values,
                  failed: !!filtered.find(
                    (value) =>
                      value.stat_date ==
                        formatDateTime(dayPast).substring(0, 10) &&
                      value.country == values.country &&
                      value.category == values.category &&
                      value.total_prices > values.total_prices &&
                      value.total_products > values.total_products
                  ),
                };
              }),
          }))
          .filter((_, idx, c) => {
            if (filters.only_failure) {
              return c.some((d) => d.items.some((item) => item.failed));
            }
            return true;
          }),
      };
    });
    return value.filter(({ dates }) =>
      dates.some(({ items }) => items.length > 0)
    );
  }, [
    initialColumns,
    filters.only_failure,
    filters.countries_in,
    filters.categories_in?.length,
  ]);

  if (current !== "imum_monitorings") {
    return <Spinner />;
  }
  // console.log({ monitorings });
  const ths = [
    <div
      style={{
        width: 160,
      }}
    >
      Category
    </div>,
    ...initialColumns.map((date) => (
      <ThColumn key={date} date={date} countries={filters.countries_in} />
    )),
  ];

  return (
    <div className="row">
      <ContentBlock containerClass="pt-3">
        <TableFilterBox
          clearFilter={() =>
            setFilters({
              ...filterInitial,
              countries_in:
                window.identifier == "technorama" ? ["LT"] : defaultLangs,
            })
          }
        >
          {window.identifier !== "technorama" && (
            <div className="px-1">
              <SelectCountry
                mode="multiple"
                style={{ width: 165 }}
                value={filters.countries_in}
                onChange={(value) => {
                  onChangeUseState(setFilters)({
                    target: {
                      name: "countries_in",
                      value,
                    },
                  });
                }}
              />
            </div>
          )}

          <div className="px-1">
            <SelectInput
              label="Product category"
              mode="multiple"
              style={{ width: 165 }}
              options={show_data?.categories || []}
              value={filters.categories_in || undefined}
              placeholder={"Enter"}
              onChange={(categories_in) => {
                setFilters((prev) => ({
                  ...prev,
                  categories_in,
                }));
              }}
            />
          </div>
          <div className="px-1">
            <Label className="text-sm-label">Show quantities</Label>
            <div className="d-flex align-items-center">
              <CheckBoxInput
                text="Products"
                checked={filters.items_products}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    items_products: !prev.items_products,
                  }))
                }
              />
              <CheckBoxInput
                text="Price changes"
                checked={filters.items_prices_changes}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    items_prices_changes: !prev.items_prices_changes,
                  }))
                }
              />
            </div>
          </div>
          <div className="px-1">
            <Label className="text-sm-label">Show only with failure</Label>
            <div>
              <CheckBoxInput
                text="Yes"
                checked={filters.only_failure}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    only_failure: !prev.only_failure,
                  }))
                }
              />
            </div>
          </div>
        </TableFilterBox>
        <div>
          <ContentValidation loader={loading} array={monitorings}>
            <TableUi className={"no-padding"} hoverable={false} ths={ths}>
              {monitorings.map((imum_monitoring) => {
                return (
                  <ItemRuleColumn
                    key={imum_monitoring.category + "_ItemRuleColumn"}
                    imum_monitoring={imum_monitoring}
                    attributes={{
                      countries: filters.countries_in,
                      show_products: filters.items_products,
                      show_prices_changes: filters.items_prices_changes,
                    }}
                  />
                );
              })}
            </TableUi>
          </ContentValidation>
        </div>
      </ContentBlock>
    </div>
  );
};

export default ImumTrackingCategories;

const ItemRuleColumn = ({ imum_monitoring, attributes }) => {
  const itsFailed = useMemo(() => {
    return imum_monitoring.dates.some(({ items }) => {
      const filtered = items.filter(({ country }) =>
        attributes.countries.includes(country.toUpperCase())
      );
      if (!filtered.length) return false;
      return filtered.some((d) => d.failed);
    });
  }, [imum_monitoring?.dates, attributes?.countries]);

  return (
    <TableUi.tr>
      <TableUi.td className={"p-left"}>
        {itsFailed && (
          <div className="position-relative">
            <Tooltip placement="left" title={"Failed"}>
              <div
                className="position-absolute rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  top: 13,
                  left: "-22px",
                  backgroundColor: "#C94747",
                  width: 15,
                  height: 15,
                  transform: "translate(0px, -50%)",
                  zIndex: 10000000000000000,
                }}
              >
                <AiOutlineExclamation className="text-xs" color="#fff" />
              </div>
            </Tooltip>
          </div>
        )}
        {imum_monitoring.category}
      </TableUi.td>
      {imum_monitoring.dates.map((item) => (
        <TableUi.td key={"imum_monitoring_ " + item.date}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              // borderRight: "1px solid #BFBFBF",
              borderLeft: "1px solid #BFBFBF",
            }}
          >
            {attributes.countries.map((country) => (
              <CountBlock
                key={"count_block_" + country}
                value={item.items?.find(
                  (s) => s.country.toLowerCase() == country.toLowerCase()
                )}
                attributes={attributes}
              />
            ))}
          </div>
        </TableUi.td>
      ))}
    </TableUi.tr>
  );
};

const CountBlock = ({ value, attributes }) => {
  if (!value) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-sm"
        style={{
          padding: "6px 5px",
          backgroundColor: "#DADADA1F",
        }}
      >
        -
      </div>
    );
  }
  return (
    <div
      className="text-sm"
      style={{
        padding: "6px 5px",
        backgroundColor: value.failed ? "#FACCCC4D" : "#DADADA1F",
      }}
    >
      {attributes.show_products && (
        <div>
          <Tooltip placement="top" title="Products">
            <IconPackageBox />
            <span
              style={{
                paddingLeft: 2,
              }}
            >
              {value.total_products}
            </span>
          </Tooltip>
        </div>
      )}
      {attributes.show_prices_changes && (
        <div>
          <Tooltip placement="top" title="Price changes">
            <IconTag />
            <span
              style={{
                paddingLeft: 2,
              }}
            >
              {value.total_prices}
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};
const ThColumn = ({ date, countries }) => {
  const active_lang = "";

  return (
    <div
      className="py-2"
      style={{
        // borderRight: "1px solid #BFBFBF",
        borderLeft: "1px solid #BFBFBF",
        // borderBottom: "1px solid #BFBFBF",
      }}
    >
      <div className="text-center font-normal my-1">
        Quantities on <span className="font-medium">{date}</span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
        }}
      >
        {countries.includes("LT") && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minWidth: 100,
              flex: 1,
            }}
          >
            <div className="d-flex justify-content-center align-items-center position-relative">
              <IconLT
                active={true}
                stroke={active_lang !== "LT" && "transparent"}
              />
            </div>
          </div>
        )}
        {countries.includes("LV") && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minWidth: 100,
              flex: 1,
            }}
          >
            <div className="d-flex justify-content-center align-items-center position-relative">
              <IconLV
                active={true}
                stroke={active_lang !== "LV" && "transparent"}
              />
            </div>
          </div>
        )}
        {countries.includes("EE") && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minWidth: 100,
              flex: 1,
            }}
          >
            <div className="d-flex justify-content-center align-items-center position-relative">
              <IconEE
                active={true}
                stroke={active_lang !== "EE" && "transparent"}
              />
            </div>
          </div>
        )}
        {countries.includes("FI") && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minWidth: 100,
              flex: 1,
            }}
          >
            <div className="d-flex justify-content-center align-items-center position-relative">
              <IconFI
                active={true}
                stroke={active_lang !== "FI" && "transparent"}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
